/**
 * Optimizer Control Center — fetch latest snapshot + restore it
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  listFlowSnapshots,
  restoreFlowSnapshot,
} from '@/features/flow-optimizer/services/flow-optimizer-api';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { invalidateTcCenterRestore } from '@/features/flow-optimizer/snapshots/restore-cache/tc-restore-cache';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

/** Lists snapshots (only while `open`) and restores the most recent one. */
export function useRestoreSnapshot(row: OptimizerRow, open: boolean) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const snapshotsQuery = useQuery({
    queryKey: ['optimizer-center', 'snapshots', row.provider, row.flowId],
    queryFn: () => listFlowSnapshots(row.flowId, row.provider),
    enabled: open,
    staleTime: 0,
  });

  const latest = snapshotsQuery.data?.snapshots?.[0] ?? null;

  const restore = useMutation({
    mutationFn: (input: { snapshotId: string; syncQueue: boolean }) =>
      restoreFlowSnapshot(row.flowId, input.snapshotId, row.provider, input.syncQueue),
    onSuccess: () => {
      toast.success('Snapshot restored', `${row.flowName} reverted to the saved state`);
      void queryClient.invalidateQueries({ queryKey: optimizerCenterKeys.all });
      void snapshotsQuery.refetch();
      invalidateTcCenterRestore(queryClient, row.provider);
    },
    onError: (err) =>
      toast.error('Restore failed', getApiErrorMessage(err, 'Could not restore the snapshot')),
  });

  return {
    latest,
    isLoading: snapshotsQuery.isLoading,
    isError: snapshotsQuery.isError,
    isRestoring: restore.isPending,
    restoreLatest: (syncQueue = true) =>
      latest && restore.mutate({ snapshotId: latest.id, syncQueue }),
  };
}
